import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { mockAlerts, mockDrivers, Alert, Severity } from '../data/mockData';
const playCriticalSound = () => {
  try {
    const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(660, ctx.currentTime + 0.1);
    gain.gain.setValueAtTime(0, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0.1, ctx.currentTime + 0.02);
    gain.gain.linearRampToValueAtTime(0.1, ctx.currentTime + 0.15);
    gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.2);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.2);
  } catch (e) {
    console.error('Audio play failed', e);
  }
};
interface ToastNotificationsProps {
  onHoverAlert?: (driverIds: string[] | null) => void;
  onClickAlert?: (alertId: string, driverId: string) => void;
  onDismissAlert?: (alertId: string, driverIds: string[]) => void;
}
// For demo: only show the first 3 critical/warning alerts
const DEMO_ALERT_LIMIT = 3;
export function ToastNotifications({
  onHoverAlert,
  onClickAlert,
  onDismissAlert
}: ToastNotificationsProps) {
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState(false);
  const playedSoundRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    let played = false;
    mockAlerts.forEach(alert => {
      if (alert.severity === 'critical' && !playedSoundRef.current.has(alert.id)) {
        playedSoundRef.current.add(alert.id);
        played = true;
      }
    });
    if (played) {
      playCriticalSound();
    }
  }, []);
  const handleDismiss = useCallback((alert: Alert) => {
    setDismissedIds(prev => {
      const next = new Set(prev);
      next.add(alert.id);
      return next;
    });
    const driverIds = alert.affectedDriverIds && alert.affectedDriverIds.length > 0 ? alert.affectedDriverIds : [alert.driverId];
    onDismissAlert?.(alert.id, driverIds);
  }, [onDismissAlert]);
  const handleToastClick = (alert: Alert) => {
    onClickAlert?.(alert.id, alert.driverId);
  };
  const handleToastHover = (alert: Alert) => {
    const ids = alert.affectedDriverIds && alert.affectedDriverIds.length > 0 ? alert.affectedDriverIds : [alert.driverId];
    onHoverAlert?.(ids);
  };
  const handleToastLeave = () => {
    onHoverAlert?.(null);
  };
  // Filter: only critical and warning, limited to demo count
  const severityOrder: Record<Severity, number> = {
    critical: 0,
    warning: 1,
    offline: 2,
    info: 3,
    normal: 4
  };
  const demoAlerts = mockAlerts.filter(a => a.severity === 'critical' || a.severity === 'warning').sort((a, b) => {
    if (severityOrder[a.severity] !== severityOrder[b.severity]) {
      return severityOrder[a.severity] - severityOrder[b.severity];
    }
    const aIndex = mockAlerts.findIndex(m => m.id === a.id);
    const bIndex = mockAlerts.findIndex(m => m.id === b.id);
    return bIndex - aIndex;
  }).slice(0, DEMO_ALERT_LIMIT);
  const activeAlerts = demoAlerts.filter(a => !dismissedIds.has(a.id));
  // Show 1 front + 2 peeking when collapsed, all when expanded
  const stackAlerts = expanded ? activeAlerts : activeAlerts.slice(0, 3);
  const hiddenCount = activeAlerts.length - 1;
  if (stackAlerts.length === 0) return null;
  return <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-50 pointer-events-none flex flex-col items-center" data-id="element-630">
      <div className="relative" style={{
      width: 450,
      height: expanded ? stackAlerts.length * 56 : 48,
      perspective: '800px',
      transition: 'height 0.3s ease'
    }} data-id="element-631">
        <AnimatePresence data-id="element-632">
          {stackAlerts.map((alert, index) => <ToastItem key={alert.id} alert={alert} stackIndex={index} expanded={expanded} onDismiss={() => handleDismiss(alert)} onClick={() => handleToastClick(alert)} onHover={() => handleToastHover(alert)} onLeave={handleToastLeave} data-id="element-633" />)}
        </AnimatePresence>
      </div>

      {/* "+N more" pill below the stack */}
      <AnimatePresence data-id="element-634">
        {hiddenCount > 0 && <motion.button initial={{
        opacity: 0,
        y: -4
      }} animate={{
        opacity: 1,
        y: 6
      }} exit={{
        opacity: 0,
        y: -4
      }} onClick={() => setExpanded(v => !v)} className="pointer-events-auto bg-slate-800/90 hover:bg-slate-700/90 text-slate-400 hover:text-slate-300 text-[11px] font-medium px-3 py-1 rounded-full border border-slate-700/50 shadow-lg transition-colors cursor-pointer" data-id="element-635">
            {expanded ? 'Show less' : `+${hiddenCount} more alert${hiddenCount > 1 ? 's' : ''}`}
          </motion.button>}
      </AnimatePresence>
    </div>;
}
function ToastItem({
  alert,
  stackIndex,
  expanded,
  onDismiss,
  onClick,
  onHover,
  onLeave
}: {
  alert: Alert;
  stackIndex: number;
  expanded: boolean;
  onDismiss: () => void;
  onClick: () => void;
  onHover: () => void;
  onLeave: () => void;
}) {
  const driver = mockDrivers.find(d => d.id === alert.driverId);
  const config = {
    critical: {
      color: 'text-brand-red',
      bg: 'bg-brand-red',
      icon: AlertTriangle
    },
    warning: {
      color: 'text-brand-amber',
      bg: 'bg-brand-amber',
      icon: AlertTriangle
    },
    offline: {
      color: 'text-slate-400',
      bg: 'bg-slate-400',
      icon: AlertTriangle
    },
    info: {
      color: 'text-brand-blue',
      bg: 'bg-brand-blue',
      icon: AlertTriangle
    },
    normal: {
      color: 'text-slate-400',
      bg: 'bg-slate-400',
      icon: AlertTriangle
    }
  }[alert.severity];
  const Icon = config.icon;
  const isFront = stackIndex === 0 || expanded;
  // Auto-dismiss after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      onDismiss();
    }, 5000);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // When expanded: full list with vertical spacing. When collapsed: iOS-style stacking
  const yOffset = expanded ? stackIndex * 56 : stackIndex * 6;
  const scale = expanded ? 1 : 1 - stackIndex * 0.03;
  const opacity = expanded ? 1 : stackIndex === 0 ? 1 : stackIndex === 1 ? 0.6 : 0.3;
  const zIndex = 50 - stackIndex;
  return <motion.div layout initial={{
    opacity: 0,
    y: 30,
    scale: 0.9
  }} animate={{
    opacity,
    y: yOffset,
    scale,
    zIndex
  }} exit={{
    opacity: 0,
    y: 30,
    scale: 0.9
  }} transition={{
    type: 'spring',
    damping: 25,
    stiffness: 300
  }} onMouseEnter={isFront ? onHover : undefined} onMouseLeave={isFront ? onLeave : undefined} onClick={isFront ? onClick : undefined} className={`absolute inset-x-0 top-0 flex items-center gap-3 bg-slate-900 border border-slate-800 rounded-lg shadow-2xl p-2 pr-2 h-12 overflow-hidden transition-colors
        ${isFront ? 'pointer-events-auto cursor-pointer hover:border-slate-700' : 'pointer-events-none'}`} style={{
    transformOrigin: 'bottom center'
  }} data-id="element-636">
      <div className={`absolute left-0 top-0 bottom-0 w-[3px] ${config.bg}`} data-id="element-637" />

      <div className={`ml-2 ${config.color} shrink-0`} data-id="element-638">
        <Icon className="w-4 h-4" data-id="element-639" />
      </div>

      <div className="flex-1 flex items-baseline gap-2 min-w-0 truncate" data-id="element-640">
        <span className="text-xs font-semibold text-slate-200 truncate" data-id="element-641">
          {alert.title}
        </span>
        {driver && <span className="text-[10px] font-mono text-slate-400 shrink-0" data-id="element-642">
            {driver.truckId}
          </span>}
        <span className="text-[10px] text-slate-500 shrink-0" data-id="element-643">
          {alert.timeContext}
        </span>
      </div>

      {isFront && <button onClick={e => {
      e.stopPropagation();
      onDismiss();
    }} className="relative flex items-center justify-center w-7 h-7 text-slate-600 hover:text-slate-300 rounded-full transition-colors shrink-0 ml-1 group" data-id="element-644">
          <X className="w-3.5 h-3.5 relative z-10 group-hover:text-slate-300" data-id="element-645" />
        </button>}
    </motion.div>;
}
// Success toast for route updates etc.
export interface SuccessToastData {
  id: string;
  message: string;
  detail?: string;
  variant?: 'success' | 'warning';
}
export function SuccessToasts({
  toasts,
  onDismiss
}: {
  toasts: SuccessToastData[];
  onDismiss: (id: string) => void;
}) {
  return <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-50 pointer-events-none flex flex-col gap-2 items-center" data-id="element-646">
      <AnimatePresence data-id="element-647">
        {toasts.map(toast => <SuccessToastItem key={toast.id} toast={toast} onDismiss={() => onDismiss(toast.id)} data-id="element-648" />)}
      </AnimatePresence>
    </div>;
}
function SuccessToastItem({
  toast,
  onDismiss
}: {
  toast: SuccessToastData;
  onDismiss: () => void;
}) {
  useEffect(() => {
    const timer = setTimeout(onDismiss, 5000);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return <motion.div initial={{
    opacity: 0,
    y: 30,
    scale: 0.95
  }} animate={{
    opacity: 1,
    y: 0,
    scale: 1
  }} exit={{
    opacity: 0,
    y: 30,
    scale: 0.95
  }} transition={{
    type: 'spring',
    damping: 25,
    stiffness: 300
  }} className={`relative pointer-events-auto flex items-center gap-3 bg-slate-900 border rounded-lg shadow-2xl p-3 pr-3 min-w-[320px] max-w-[400px] overflow-hidden ${toast.variant === 'warning' ? 'border-brand-amber/30' : 'border-brand-green/30'}`} data-id="element-649">
      <div className={`absolute left-0 top-0 bottom-0 w-[3px] rounded-l-lg ${toast.variant === 'warning' ? 'bg-brand-amber' : 'bg-brand-green'}`} data-id="element-650" />
      <div className={`ml-2 shrink-0 ${toast.variant === 'warning' ? 'text-brand-amber' : 'text-brand-green'}`} data-id="element-651">
        {toast.variant === 'warning' ? <AlertTriangle className="w-4 h-4" data-id="element-652" /> : <CheckCircle2 className="w-4 h-4" data-id="element-653" />}
      </div>
      <div className="flex-1 min-w-0" data-id="element-654">
        <p className="text-xs font-semibold text-slate-200" data-id="element-655">{toast.message}</p>
        {toast.detail && <p className="text-[10px] text-slate-500 mt-0.5" data-id="element-656">{toast.detail}</p>}
      </div>
      <button onClick={onDismiss} className="flex items-center justify-center w-7 h-7 text-slate-600 hover:text-slate-300 rounded-full transition-colors shrink-0 group" data-id="element-657">
        <X className="w-3.5 h-3.5 group-hover:text-slate-300" data-id="element-658" />
      </button>
    </motion.div>;
}